import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation, useHistory } from 'react-router-dom';
import moment from 'moment';
import swal from 'sweetalert';
import { getPackages, deletePackage } from '../../api/packageApi';
import { Pagination } from 'semantic-ui-react';

const EMPTY_MSG = 'No services yet. Click Add to create your first one.';
const PER_PAGE = 20;

export default function PartnerMyPrivateTours() {
  const history = useHistory();
  const location = useLocation();
  const filtersRef = useRef({ search: '', published: '' });
  const [list, setList] = useState([]);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [filters, setFilters] = useState({ search: '', published: '' });
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  filtersRef.current = filters;

  const fetchList = useCallback((params = {}) => {
    setLoading(true);
    setError(false);
    const f = filtersRef.current;
    const page = params.page || new URLSearchParams(location.search).get('page') || 1;
    const q = {
      created_by_me: 1,
      per_page: PER_PAGE,
      page,
      ...(f.search ? { 'q[name_cont]': f.search } : {}),
      ...(f.published ? { published: f.published } : {}),
      sort_by: params.sort_by ?? sortBy,
      sort_order: params.sort_order ?? sortOrder,
      ...params
    };
    getPackages(q)
      .then((res) => {
        const raw = (res.data && res.data.packages) || res.data;
        const arr = Array.isArray(raw) ? raw : (raw && raw.data) || [];
        setList(arr);
        setMeta(res.data?.meta?.pagination || res.data?.meta || {});
      })
      .catch(() => {
        setError(true);
        setList([]);
      })
      .finally(() => setLoading(false));
  }, [sortBy, sortOrder, location.search]);

  useEffect(() => {
    fetchList({});
  }, [fetchList]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    history.replace({ pathname: location.pathname, search: '?page=1' });
  };

  const handlePageChange = (e, { activePage }) => {
    history.push({ pathname: location.pathname, search: `?page=${activePage}` });
    fetchList({ page: activePage });
  };

  const handleSort = (col) => {
    const nextOrder = sortBy === col && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortBy(col);
    setSortOrder(nextOrder);
    fetchList({ sort_by: col, sort_order: nextOrder, page: 1 });
    history.replace({ pathname: location.pathname, search: '?page=1' });
  };

  const applyFilters = () => {
    history.replace({ pathname: location.pathname, search: '?page=1' });
    fetchList({ page: 1 });
  };

  const handleDelete = (item) => {
    const id = item.idx || item.id;
    if (!id) return;
    swal({
      title: 'Are you sure?',
      text: 'This private tour will be removed.',
      icon: 'warning',
      buttons: true,
      dangerMode: true
    }).then((ok) => {
      if (!ok) return;
      setDeletingId(id);
      deletePackage(id)
        .then(() => {
          swal('Deleted', { icon: 'success' });
          fetchList({});
        })
        .catch((e) => {
          const msg = (e.response && e.response.data && (e.response.data.errors || e.response.data.message)) || 'Delete failed';
          swal('Error', Array.isArray(msg) ? msg.join(' ') : msg, 'error');
        })
        .finally(() => setDeletingId(null));
    });
  };

  const pag = meta.pagination || meta || {};
  const totalEntries = pag.total_entries ?? pag.total_count ?? list.length;
  const currentPage = parseInt(pag.current_page || 1, 10);
  const totalPages = Math.max(1, parseInt(pag.total_pages || 1, 10));
  const from = list.length === 0 ? 0 : (currentPage - 1) * PER_PAGE + 1;
  const to = (currentPage - 1) * PER_PAGE + list.length;

  const SortableTh = ({ col, label }) => (
    <th
      className="cursor-pointer"
      onClick={() => handleSort(col)}
      style={{ cursor: 'pointer', userSelect: 'none' }}
      title={`Sort by ${label}`}
    >
      {label}
      {sortBy === col && <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
    </th>
  );

  return (
    <div className="container py-4">
      <Link to="/partner" className="btn btn-outline-secondary btn-sm mb-3">← Back to Dashboard</Link>
      <div className="d-flex flex-wrap align-items-center justify-content-between mb-4">
        <div>
          <h2 className="mb-0">My Private Tours</h2>
          <p className="text-muted small mb-0 mt-1">Edit and Delete are only available when no one has inquired or booked.</p>
        </div>
        <Link to="/partner/private-tours/new" className="btn btn-primary">➕ Add Private Tour</Link>
      </div>

      <div className="card mb-3">
        <div className="card-body py-2">
          <div className="d-flex flex-wrap align-items-end gap-2">
            <div>
              <label className="small text-muted d-block">Title</label>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                onBlur={applyFilters}
                style={{ width: 160 }}
              />
            </div>
            <div>
              <label className="small text-muted d-block">Published</label>
              <select
                className="form-control form-control-sm"
                value={filters.published}
                onChange={(e) => { handleFilterChange('published', e.target.value); applyFilters(); }}
                style={{ width: 100 }}
              >
                <option value="">All</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            </div>
            <button type="button" className="btn btn-sm btn-outline-primary" onClick={applyFilters}>Apply</button>
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-warning">
          Could not load list. <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => fetchList({})}>Retry</button>
        </div>
      )}

      {loading && list.length === 0 && (
        <p className="text-muted">Loading…</p>
      )}

      {!loading && !error && list.length === 0 && (
        <div className="card">
          <div className="card-body text-center py-5">
            <p className="text-muted mb-0">{EMPTY_MSG}</p>
            <Link to="/partner/private-tours/new" className="btn btn-primary mt-3">Add Private Tour</Link>
          </div>
        </div>
      )}

      {!loading && list.length > 0 && (
        <>
          <div className="card">
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="thead-light">
                    <tr>
                      <SortableTh col="name" label="Title" />
                      <th>From → To</th>
                      <th>Price (total)</th>
                      <SortableTh col="published" label="Published" />
                      <SortableTh col="created_at" label="Created at" />
                      <th className="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((p) => (
                      <tr key={p.idx || p.id}>
                        <td>{p.name || '—'}</td>
                        <td>{p.from_to_display || (p.origin && p.destination ? `${p.origin} → ${p.destination}` : (p.origin || p.destination || '—'))}</td>
                        <td>{p.vendor_total_price != null ? `${Number(p.vendor_total_price).toLocaleString()} ${p.currency_code || 'NPR'}` : (p.price != null ? `${Number(p.price).toLocaleString()} ${p.currency_code || 'NPR'}` : '—')}</td>
                        <td>{p.published ? 'Yes' : 'No'}</td>
                        <td>{p.created_at ? moment(p.created_at).format('D MMM YYYY') : '—'}</td>
                        <td className="text-right">
                          {p.has_any_inquiry_or_booking ? (
                            <span className="text-muted small" title="Not available once anyone has inquired or booked">Edit / Delete locked</span>
                          ) : (
                            <>
                              <Link to={`/partner/private-tours/edit/${p.idx || p.id}`} className="btn btn-sm btn-outline-primary mr-1">✏ Edit</Link>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleDelete(p)}
                                disabled={deletingId === (p.idx || p.id)}
                              >
                                🗑 Delete
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div className="d-flex justify-content-between align-items-center mt-2 flex-wrap gap-2">
            <span className="text-muted small">Showing {from}–{to} of {totalEntries} records</span>
            {totalPages > 1 && (
              <Pagination
                activePage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                firstItem={null}
                lastItem={null}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}
